/**
 * ==================== 视频参考图纯逻辑模块（可独立测试） ====================
 *
 * 职责：构建画布视频任务（含首尾帧/多模态）的参考素材集合与展示条目。
 * 纯函数，不依赖画布运行时（fabric / store / 网络），node 环境可直接单测。
 *
 * 被依赖：task.ts（导入 sanitizeUrl / resolveCanvasVideoMode /
 *         resolveCanvasVideoReferenceCollections，并 re-export
 *         resolveCanvasVideoMode / buildCanvasVideoReferenceItems 保持对外 API）
 */

import { ModuleId } from '@xmiles/holopix-types'
import type { CanvasVideoMode, CanvasVideoReferenceItem } from './types'

const VIDEO_TOKEN_REGEX = /@?(图|图片|视频)(\d+)/g

/** prompt 中视频引用槽位数（取最大序号，兼容有/无 @） */
const countPromptVideoSlots = (text: string): number => {
  const re = /@?视频(\d+)/g
  let max = 0
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    const num = Number(match[1])
    if (Number.isFinite(num) && num > max) max = num
  }
  return max
}

export const sanitizeUrl = (value: unknown): string => {
  return String(value ?? '').trim()
}

const normalizeReferenceUrlKey = (value: unknown): string => {
  const url = sanitizeUrl(value)
  if (!url) return ''
  return url.split('#')[0].split('?')[0] || url
}

const appendUniqueUrls = (target: string[], values: unknown[]) => {
  values.forEach((value) => {
    const url = sanitizeUrl(value)
    if (url && !target.includes(url)) {
      target.push(url)
    }
  })
}

const pickUrlArray = (...candidates: unknown[]): string[] => {
  const urls: string[] = []
  candidates.forEach((candidate) => {
    if (!Array.isArray(candidate)) return
    appendUniqueUrls(urls, candidate)
  })
  return urls
}

export const resolveCanvasVideoMode = (
  params?: Record<string, any> | null,
): CanvasVideoMode => {
  const moduleId = Number(
    params?.module ?? params?.moduleId ?? params?.modelId,
  )
  // 首尾帧模式（双模型 + mini + H3 首尾帧/图生）
  if (
    moduleId === Number(ModuleId.CanvasStartEndVideo) ||
    moduleId === Number(ModuleId.SeedanceStartEndVideo) ||
    moduleId === Number(ModuleId.SeedanceMiniStartEndVideo) ||
    moduleId === Number(ModuleId.MinimaxH3StartEndVideo) ||
    moduleId === Number(ModuleId.MinimaxH3FastStartEndVideo) ||
    moduleId === Number(ModuleId.MinimaxH3MaxStartEndVideo)
  ) {
    return 'head-tail'
  }
  // Vidu 图生视频：单帧，走 head-tail 回填
  if (moduleId === Number(ModuleId.CanvasImage2Video)) {
    return 'head-tail'
  }
  // MiniMax H3 图生视频（85）：单帧首图，走 head-tail 回填（对齐 Vidu 图生）
  if (moduleId === Number(ModuleId.MinimaxH3Image2Video) || moduleId === Number(ModuleId.MinimaxH3FastImage2Video) || moduleId === Number(ModuleId.MinimaxH3MaxImage2Video)) {
    return 'head-tail'
  }
  // Seedance 图生视频（含 mini）：多图，走 multimodal 回填（R4）
  if (
    moduleId === Number(ModuleId.SeedanceImage2Video) ||
    moduleId === Number(ModuleId.SeedanceMiniImage2Video)
  ) {
    return 'multimodal'
  }
  if (
    moduleId === Number(ModuleId.CanvasReferenceVideo) ||
    moduleId === Number(ModuleId.SeedanceReferenceVideo) ||
    moduleId === Number(ModuleId.SeedanceMiniReferenceVideo) ||
    moduleId === Number(ModuleId.MinimaxH3ReferenceVideo) ||
    moduleId === Number(ModuleId.MinimaxH3FastReferenceVideo)
  ) {
    return 'multimodal'
  }
  // 文生视频模块(64/72/86)重编辑归位到内部 CanvasVideoMode 的 'multimodal'（对应全能参考内部模式）。
  // 注意：此处返回内部枚举值，与 resolveVideoModeByModuleId 返回的外部 'reference' 分属两套枚举，
  // 两者均指"全能参考"，不冲突；详见 data-model.md 第 3.5 节双层枚举说明。
  if (
    moduleId === Number(ModuleId.SeedanceText2Video) ||
    moduleId === Number(ModuleId.SeedanceMiniText2Video) ||
    moduleId === Number(ModuleId.MinimaxH3Text2Video) ||
    moduleId === Number(ModuleId.MinimaxH3FastText2Video) ||
    moduleId === Number(ModuleId.MinimaxH3MaxText2Video)
  ) {
    return 'multimodal'
  }
  const explicitMode = sanitizeUrl(params?.videoMode ?? params?.mode)
  if (explicitMode === 'head-tail' || explicitMode === 'multimodal') {
    return explicitMode
  }
  const imageList = Array.isArray(params?.imageList)
    ? params?.imageList
    : []
  const hasLastFrame =
    Boolean(params?.lastFrameUrl) || imageList.length > 1
  return hasLastFrame ? 'head-tail' : 'multimodal'
}

export const resolveCanvasVideoReferenceCollections = (
  params?: Record<string, any> | null,
): { imageUrls: string[]; videoUrls: string[] } => {
  const mode = resolveCanvasVideoMode(params)
  const imageUrls: string[] = []
  const videoUrls: string[] = []

  const imageCandidates = pickUrlArray(
    params?.multimodalImageUrls,
    params?.imageList,
  )
  const firstFrameUrl = sanitizeUrl(
    params?.firstFrameUrl ?? params?.firstImageUrl ?? params?.originImgUrl,
  )
  const lastFrameUrl = sanitizeUrl(params?.lastFrameUrl ?? params?.lastImageUrl)
  // 只有首尾帧模式才把尾帧当作独立参考；多模态模式下尾帧只是普通参考图之一，
  // 不能把它从参考图列表里过滤掉，否则上传 3 张图时会只显示 2 张。
  const frameUrlKeys =
    mode === 'head-tail'
      ? new Set([firstFrameUrl, lastFrameUrl].map(normalizeReferenceUrlKey).filter(Boolean))
      : new Set([firstFrameUrl].map(normalizeReferenceUrlKey).filter(Boolean))
  const referenceImageCandidates = imageCandidates.filter((url) => !frameUrlKeys.has(normalizeReferenceUrlKey(url)))

  if (mode === 'head-tail') {
    // 首帧/尾帧是两个独立槽位，即使 URL 相同也必须都保留
    // （例如同一张图同时作为首帧与尾帧），去重只作用于额外参考图
    const frameSlots = [firstFrameUrl, lastFrameUrl].filter(Boolean)
    frameSlots.forEach((url) => imageUrls.push(url))
    appendUniqueUrls(imageUrls, referenceImageCandidates)
  } else {
    appendUniqueUrls(imageUrls, [firstFrameUrl, ...referenceImageCandidates])
  }

  const selectedVideoUrl = sanitizeUrl(
    params?.selectedVideoUrl ?? params?.videoUrl,
  )
  const videoCandidates = pickUrlArray(
    params?.multimodalVideoUrls,
    params?.videos,
  )
  appendUniqueUrls(videoUrls, [selectedVideoUrl, ...videoCandidates])

  return { imageUrls, videoUrls }
}

export const buildCanvasVideoReferenceItems = (
  params?: Record<string, any> | null,
  promptText?: string,
  visibleUrls?: string[],
): CanvasVideoReferenceItem[] => {
  const { imageUrls, videoUrls } =
    resolveCanvasVideoReferenceCollections(params)

  // 展示层可见性过滤：仅保留用户可见素材（imageRefs 已按设计排除 hidden 注入图）。
  // 未提供 visibleUrls 或为空时不过滤，兼容无 imageRefs 的历史记录。
  const visibleKeySet =
    Array.isArray(visibleUrls) && visibleUrls.length > 0
      ? new Set(visibleUrls.map(normalizeReferenceUrlKey).filter(Boolean))
      : null
  const isVisible = (url: string) =>
    !visibleKeySet || visibleKeySet.has(normalizeReferenceUrlKey(url))

  // 优先按 imageRefs 逐条生成图片项：imageList/multimodal 经 URL 去重后可能只剩 1 条，
  // 但 prompt 仍有 @图片1..N；imageRefs 保留了与 token 对应的条目数。
  const imageRefs = Array.isArray(params?.imageRefs) ? params.imageRefs : null
  const imageItems =
    imageRefs && imageRefs.length > 0
      ? imageRefs
          .map((ref: { url?: string }) => sanitizeUrl(ref?.url))
          .filter((url: string) => Boolean(url) && isVisible(url))
          .map((url: string, index: number) => ({
            label: `图${index + 1}`,
            url,
            mediaType: 'image' as const,
          }))
      : imageUrls.filter(isVisible).map((url, index) => ({
          label: `图${index + 1}`,
          url,
          mediaType: 'image' as const,
        }))

  // imageRefs 只收录用户可见图片（排除 hidden 注入图），不含 @ 引用的视频。
  // 视频条数以 prompt 中 视频N 槽位为准；URL 取去重后的 videoUrls。
  // 避免：videos 原数组膨胀（4 条）导致缩略图多于 prompt；或同 URL 去重后少于 prompt 槽位。
  const text = sanitizeUrl(promptText ?? params?.prompt)
  const promptVideoSlots = countPromptVideoSlots(text)
  const videoCount = promptVideoSlots > 0 ? promptVideoSlots : videoUrls.length
  const videoItems = Array.from({ length: videoCount }, (_, index) => {
    const url = videoUrls[index] ?? videoUrls[videoUrls.length - 1] ?? ''
    return {
      label: `视频${index + 1}`,
      url,
      mediaType: 'video' as const,
    }
  }).filter((item) => item.url)

  const itemMap = new Map<string, CanvasVideoReferenceItem>()
  imageItems.forEach((item, index) => {
    itemMap.set(item.label, item)
    itemMap.set(`图片${index + 1}`, item)
  })
  videoItems.forEach((item) => {
    itemMap.set(item.label, item)
  })

  const orderedItems: CanvasVideoReferenceItem[] = []
  const appendedLabels = new Set<string>()
  let match: RegExpExecArray | null
  const tokenRegex = new RegExp(VIDEO_TOKEN_REGEX.source, 'g')

  while ((match = tokenRegex.exec(text)) !== null) {
    const label = `${match[1]}${match[2]}`
    const item = itemMap.get(label)
    if (!item || appendedLabels.has(item.label)) continue
    orderedItems.push(item)
    appendedLabels.add(item.label)
  }

  ;[...imageItems, ...videoItems].forEach((item) => {
    if (appendedLabels.has(item.label)) return
    orderedItems.push(item)
  })

  return orderedItems
}
